import { ReportConfig } from './reports'

export interface GuideRecipe {
  id: string
  label: string
  description: string
  config: ReportConfig
}

export interface GuideTopic {
  id: string
  datasetId: string
  label: string
  description: string
  /** Shown as a hint of what we'll pre-select. */
  popularFields: string[]
  recipes: GuideRecipe[]
}

/**
 * Curated guided-start topics for the report builder. Each topic maps a
 * plain-language question ("Are you looking for appointment information?")
 * to a dataset, with a few popular ready-made views that pre-fill the builder.
 */
export const guideTopics: GuideTopic[] = [
  {
    id: 'appointments',
    datasetId: 'appointments',
    label: 'Appointment Information',
    description:
      'Bookings, collection sites, statuses, no-shows, and scheduling detail.',
    popularFields: ['Participant', 'Company', 'Collection Site', 'Status'],
    recipes: [
      {
        id: 'appt-detail',
        label: 'Detailed appointment list',
        description:
          'One row per appointment with the most-used columns pre-selected.',
        config: {
          datasetId: 'appointments',
          joinId: null,
          columns: [
            'participant',
            'company',
            'testType',
            'collectionSite',
            'status',
            'apptDate',
          ],
          filters: [],
          groupBy: null,
          metricField: null,
          metricFn: 'count',
          chartType: 'table',
        },
      },
      {
        id: 'appt-by-status',
        label: 'Totals by status',
        description:
          'How many appointments completed, no-showed, or were rescheduled.',
        config: {
          datasetId: 'appointments',
          joinId: null,
          columns: ['status'],
          filters: [],
          groupBy: 'status',
          metricField: null,
          metricFn: 'count',
          chartType: 'pie',
        },
      },
      {
        id: 'appt-by-site',
        label: 'Totals by collection site',
        description: 'Appointment volume at each collection site.',
        config: {
          datasetId: 'appointments',
          joinId: null,
          columns: ['collectionSite'],
          filters: [],
          groupBy: 'collectionSite',
          metricField: null,
          metricFn: 'count',
          chartType: 'bar',
        },
      },
    ],
  },
  {
    id: 'results',
    datasetId: 'results',
    label: 'Test Result Information',
    description:
      'Lab-confirmed outcomes, test categories, labs, and turnaround times.',
    popularFields: ['Participant', 'Test Category', 'Result', 'Laboratory'],
    recipes: [
      {
        id: 'results-detail',
        label: 'Detailed results list',
        description:
          'One row per test result with the most-used columns pre-selected.',
        config: {
          datasetId: 'results',
          joinId: null,
          columns: [
            'participant',
            'company',
            'testCategory',
            'result',
            'lab',
            'collectedDate',
          ],
          filters: [],
          groupBy: null,
          metricField: null,
          metricFn: 'count',
          chartType: 'table',
        },
      },
      {
        id: 'results-by-outcome',
        label: 'Totals by outcome',
        description:
          'Breakdown of negative, non-negative, positive, and cancelled results.',
        config: {
          datasetId: 'results',
          joinId: null,
          columns: ['result'],
          filters: [],
          groupBy: 'result',
          metricField: null,
          metricFn: 'count',
          chartType: 'pie',
        },
      },
      {
        id: 'results-turnaround',
        label: 'Average turnaround by lab',
        description: 'How quickly each laboratory returns results, in hours.',
        config: {
          datasetId: 'results',
          joinId: null,
          columns: ['lab', 'turnaroundHrs'],
          filters: [],
          groupBy: 'lab',
          metricField: 'turnaroundHrs',
          metricFn: 'avg',
          chartType: 'bar',
        },
      },
    ],
  },
  {
    id: 'clearances',
    datasetId: 'clearances',
    label: 'Clearance Information',
    description:
      'Pre-access, site entry, and return-to-duty clearance requests.',
    popularFields: ['Participant', 'Clearance Type', 'Status'],
    recipes: [
      {
        id: 'clearance-detail',
        label: 'Detailed clearance list',
        description:
          'One row per clearance request with the most-used columns pre-selected.',
        config: {
          datasetId: 'clearances',
          joinId: null,
          columns: [
            'participant',
            'company',
            'clearanceType',
            'status',
            'requestedDate',
          ],
          filters: [],
          groupBy: null,
          metricField: null,
          metricFn: 'count',
          chartType: 'table',
        },
      },
      {
        id: 'clearance-by-status',
        label: 'Totals by status',
        description: 'Cleared vs. pending, denied, and expired requests.',
        config: {
          datasetId: 'clearances',
          joinId: null,
          columns: ['status'],
          filters: [],
          groupBy: 'status',
          metricField: null,
          metricFn: 'count',
          chartType: 'bar',
        },
      },
      {
        id: 'clearance-processing',
        label: 'Average processing time by type',
        description: 'How many days each clearance type takes to process.',
        config: {
          datasetId: 'clearances',
          joinId: null,
          columns: ['clearanceType', 'processingDays'],
          filters: [],
          groupBy: 'clearanceType',
          metricField: 'processingDays',
          metricFn: 'avg',
          chartType: 'bar',
        },
      },
    ],
  },
  {
    id: 'experience',
    datasetId: 'surveys',
    label: 'Participant Experience',
    description:
      'Post-appointment satisfaction ratings and participant comments.',
    popularFields: ['Collection Site', 'Rating', 'Comment'],
    recipes: [
      {
        id: 'survey-detail',
        label: 'Detailed survey list',
        description:
          'One row per survey with ratings and comments pre-selected.',
        config: {
          datasetId: 'surveys',
          joinId: null,
          columns: [
            'participant',
            'company',
            'collectionSite',
            'rating',
            'comment',
            'surveyDate',
          ],
          filters: [],
          groupBy: null,
          metricField: null,
          metricFn: 'count',
          chartType: 'table',
        },
      },
      {
        id: 'survey-by-site',
        label: 'Average rating by site',
        description: 'Which collection sites participants rate the highest.',
        config: {
          datasetId: 'surveys',
          joinId: null,
          columns: ['collectionSite', 'rating'],
          filters: [],
          groupBy: 'collectionSite',
          metricField: 'rating',
          metricFn: 'avg',
          chartType: 'bar',
        },
      },
      {
        id: 'survey-by-company',
        label: 'Average rating by company',
        description: 'Satisfaction levels across each client company.',
        config: {
          datasetId: 'surveys',
          joinId: null,
          columns: ['company', 'rating'],
          filters: [],
          groupBy: 'company',
          metricField: 'rating',
          metricFn: 'avg',
          chartType: 'bar',
        },
      },
    ],
  },
]
